/*!
 * FlexGuard Builder v1.3 - Auswara
 * Build-your-own medical ID bracelet: band colour, finish, medical charm,
 * engraving with a live mm-accurate preview, font and extras.
 * The photo of the chosen combination sits above the live engraving preview.
 * Vanilla JS, no dependencies. Uses the Shopify Ajax Cart API.
 */
(function () {
  'use strict';

  /* ------------------------------------------------------------------ *
   * Constants (all physical sizes in millimetres)
   * ------------------------------------------------------------------ */
  var U = 10;                                   // SVG user units per mm
  var STAGE = { w: 50, h: 28 };                 // engraving preview frame, under the photo
  var BAND_W = 18;                              // silicone band width
  var CHARM = { w: 8.4, h: 19.45, gap: 0.6, dy: 0.15, r: 0.8 };
  var PLATE_R = 1.1;                            // plate corner radius
  var SVGNS = 'http://www.w3.org/2000/svg';
  var XMLNS = 'http://www.w3.org/XML/1998/namespace';

  // Preview faces. The CSS families are declared with @font-face in the
  // section: real font first via local(), then a free same-width twin.
  var FACES = {
    arial:   { css: '"FG Arial"',   stack: '"FG Arial", Arial, Arimo, Helvetica, sans-serif' },
    century: { css: '"FG Century"', stack: '"FG Century", Century, "Century Schoolbook", serif' },
    times:   { css: '"FG Times"',   stack: '"FG Times", "Times New Roman", Tinos, Times, serif' },
    comic:   { css: '"FG Comic"',   stack: '"FG Comic", "Comic Sans MS", cursive' },
    mvboli:  { css: '"FG MV Boli"', stack: '"FG MV Boli", "MV Boli", cursive',
               local: ['MV Boli', 'MVBoli'], twinStroke: 0.024, twinNote: true }
  };

  var FALLBACK_COLOURS = {
    black: '#1c1c1f', white: '#f2f0eb', red: '#ec1c36', orange: '#f77a0d', yellow: '#f2d80e',
    'lime green': '#8ee21a', green: '#2bb24c', 'emerald green': '#0cc497', 'light blue': '#1fb0ec',
    'medium blue': '#1f47d1', 'dark blue': '#1f2f5c', navy: '#1f2f5c', purple: '#c87ae9',
    pink: '#f59bc9', grey: '#8d9096', gray: '#8d9096'
  };

  var FALLBACK_FINISHES = {
    silver:      { light: '#f4f5f6', dark: '#a9adb3', text: '#34373c' },
    black:       { light: '#4a4b4f', dark: '#141416', text: '#e6e7e9' },
    gold:        { light: '#f3dc9f', dark: '#c49a4a', text: '#4f3a17' },
    'rose gold': { light: '#f5cfbf', dark: '#c48b74', text: '#57352a' }
  };

  var T = {
    bandTitle: 'Band colour',
    finishTitle: 'Plate & clasp finish',
    finishHelp: 'Your plate, clasp and medical charm all come in this finish.',
    charmTitle: 'Medical alert charm',
    charmAdd: 'Add the charm',
    charmAddMeta: 'Red medical symbol, slides on beside the plate',
    charmNone: 'No charm',
    charmNoneMeta: 'Engraved plate only',
    charmBadge: 'Recommended',
    charmAdded: 'Added',
    charmChip: 'Medical charm',
    lineOne: '1 line',
    lineMany: '{n} lines',
    smallHelp: 'Shorter lines engrave bigger. Try splitting your longest line.',
    charmHelp: 'The red medical symbol helps paramedics and first responders recognise a medical ID at a glance.',
    engraveTitle: 'Your engraving',
    engraveLabel: 'Type it exactly as you want it engraved. Press Enter for a new line.',
    fontTitle: 'Font',
    extrasTitle: 'Extras',
    optional: 'Optional',
    quickAdd: 'Quick add',
    sizeLabel: 'Text size on plate',
    sizeGreat: 'Easy to read',
    sizeGood: 'Good',
    sizeSmall: 'Small, still clear',
    sizeTooSmall: 'Too small to engrave clearly',
    tooSmallHelp: 'Shorten your longest line or split it over two lines to make the text bigger.',
    previewNote: 'Preview is a close guide. Final spacing may vary slightly.',
    twinNote: 'Shown in a close match on this device. Engraved in MV Boli.',
    example: 'Example',
    livePreview: 'Engraving preview',
    viewBracelet: 'Yours',
    peekLabel: 'Your bracelet',
    peekTap: 'See your bracelet',
    comboAlt: '{band} band with {finish} plate and clasp',
    comboAltCharm: '{band} band with {finish} plate, clasp and medical charm',
    zoomIn: 'Zoom in on the engraving',
    zoomOut: 'Show the whole plate',
    backToDesign: 'Back to your bracelet',
    yourDesign: 'Your bracelet',
    summaryTitle: 'Your FlexGuard',
    total: 'Total',
    each: 'each',
    addToCart: 'Add to cart',
    adding: 'Adding...',
    added: 'Added to your cart',
    soldOut: 'Sold out',
    unavailable: 'This combination is sold out. Choose another band or finish.',
    needBand: 'Choose a band colour.',
    needFinish: 'Choose a finish.',
    needCharm: 'Choose whether to add the medical charm.',
    needText: 'Add your engraving.',
    needConfirm: 'Tick the box to confirm your engraving.',
    maxLines: 'Maximum {n} lines.',
    maxChars: '{n} character limit reached.',
    emojiRemoved: 'Emojis can\u2019t be engraved, so they were removed.',
    charsCount: '{n} / {max} characters',
    linesCount: '{n} / {max} lines',
    spareTitle: 'Spare silicone band',
    spareText: 'Swap colours to suit your outfit, or keep one as a spare. Cut to fit, same as your main band.',
    spareSelected: '{n} selected: {list}',
    spareNone: 'Tap colours to add spare bands.',
    spareMax: 'Up to {n} spare bands per bracelet.',
    freeShipYes: 'This order ships free within Australia.',
    freeShipNo: 'Add {amount} more for free shipping within Australia.',
    bracelet: 'Bracelet',
    band: 'band',
    finish: 'finish',
    forLabel: 'For',
    error: 'Something went wrong adding this to your cart. Please try again.'
  };

  /* ------------------------------------------------------------------ *
   * Small helpers
   * ------------------------------------------------------------------ */
  function h(tag, attrs, kids) {
    var n = document.createElement(tag);
    setAttrs(n, attrs);
    append(n, kids);
    return n;
  }
  function sv(tag, attrs, kids) {
    var n = document.createElementNS(SVGNS, tag);
    setAttrs(n, attrs);
    append(n, kids);
    return n;
  }
  function setAttrs(n, attrs) {
    if (!attrs) return;
    Object.keys(attrs).forEach(function (k) {
      var v = attrs[k];
      if (v === null || v === undefined || v === false) return;
      if (k === 'class') n.setAttribute('class', v);
      else if (k === 'text') n.textContent = v;
      else if (k === 'html') n.innerHTML = v;
      else if (k === 'style' && typeof v === 'object') Object.keys(v).forEach(function (p) { n.style.setProperty(p, v[p]); });
      else if (k.indexOf('on') === 0 && typeof v === 'function') n.addEventListener(k.slice(2), v);
      else n.setAttribute(k, v === true ? '' : String(v));
    });
  }
  function append(n, kids) {
    (kids || []).forEach(function (c) {
      if (c === null || c === undefined || c === false) return;
      n.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
    });
  }
  function norm(s) { return String(s === undefined || s === null ? '' : s).trim().toLowerCase(); }
  function handleize(s) { return norm(s).replace(/['"]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, ''); }
  function fmt(str, vars) { return String(str).replace(/\{(\w+)\}/g, function (m, k) { return vars[k] !== undefined ? vars[k] : m; }); }
  function clamp(v, a, b) { return Math.max(a, Math.min(b, v)); }
  function lines(str) { return String(str || '').split(/\r?\n/).map(function (s) { return s.trim(); }).filter(Boolean); }
  function chars(str) { return Array.from(String(str || '').replace(/\n/g, '')).length; }
  function decodeEntities(str) { var t = document.createElement('textarea'); t.innerHTML = str || ''; return t.value; }

  function hexToRgb(hex) {
    var m = String(hex || '').replace('#', '').trim();
    if (m.length === 3) m = m.split('').map(function (c) { return c + c; }).join('');
    var n = parseInt(m.slice(0, 6), 16);
    if (isNaN(n)) return { r: 128, g: 128, b: 128 };
    return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
  }
  function rgbToHex(c) {
    return '#' + [c.r, c.g, c.b].map(function (v) { return ('0' + Math.round(clamp(v, 0, 255)).toString(16)).slice(-2); }).join('');
  }
  function mix(a, b, t) {
    var x = hexToRgb(a), y = hexToRgb(b);
    return rgbToHex({ r: x.r + (y.r - x.r) * t, g: x.g + (y.g - x.g) * t, b: x.b + (y.b - x.b) * t });
  }
  function shade(hex, amt) { return amt >= 0 ? mix(hex, '#ffffff', amt) : mix(hex, '#000000', -amt); }
  function luminance(hex) {
    var c = hexToRgb(hex);
    var f = function (v) { v /= 255; return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); };
    return 0.2126 * f(c.r) + 0.7152 * f(c.g) + 0.0722 * f(c.b);
  }

  function formatMoney(cents, format) {
    var tpl = decodeEntities(String(format || '${{amount}}').replace(/<[^>]*>/g, ''));
    var re = /\{\{\s*(\w+)\s*\}\}/;
    function delim(num, precision, thousands, decimal) {
      var n = (num / 100).toFixed(precision);
      var p = n.split('.');
      return p[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands) + (p[1] ? decimal + p[1] : '');
    }
    var m = tpl.match(re);
    var v;
    switch (m ? m[1] : 'amount') {
      case 'amount_no_decimals': v = delim(cents, 0, ',', '.'); break;
      case 'amount_with_comma_separator': v = delim(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': v = delim(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': v = delim(cents, 2, '\'', '.'); break;
      default: v = delim(cents, 2, ',', '.');
    }
    return m ? tpl.replace(re, v) : '$' + v;
  }

  // Parse "Name: #hex" or "Name | #hex" lines.
  function parseSwatches(text) {
    var out = [];
    lines(text).forEach(function (l) {
      var m = l.match(/^(.*?)\s*[:|]\s*(#[0-9a-f]{3,8})\s*$/i);
      if (m) out.push({ name: m[1].trim(), hex: m[2] });
    });
    return out;
  }
  // "Name | light | dark | engraving colour"
  function parseFinishStyles(text) {
    var out = {};
    lines(text).forEach(function (l) {
      var p = l.split('|').map(function (s) { return s.trim(); });
      if (p.length >= 4) out[norm(p[0])] = { name: p[0], light: p[1], dark: p[2], text: p[3] };
    });
    return out;
  }
  // "Label | face | Name sent to order"
  function parseFonts(text) {
    var out = [];
    lines(text).forEach(function (l) {
      var p = l.split('|').map(function (s) { return s.trim(); });
      if (!p[0]) return;
      out.push({ label: p[0], key: norm(p[1] || p[0]).replace(/\s+/g, ''), engrave: p[2] || p[0] });
    });
    return out;
  }

  // Remove emoji and pictographs (they can't be engraved).
  var PICTO;
  try { PICTO = new RegExp('[\\p{Extended_Pictographic}\\u{1F1E6}-\\u{1F1FF}\\u{FE0F}\\u{200D}\\u{20E3}]', 'gu'); }
  catch (e) { PICTO = /[\uD83C-\uDBFF][\uDC00-\uDFFF]|[\u2600-\u27bf]|\ufe0f|\u200d/g; }

  /* ------------------------------------------------------------------ *
   * Text measurement (canvas, using the same faces as the preview)
   * ------------------------------------------------------------------ */
  var Measure = {
    ctx: null,
    cache: {},
    reset: function () { this.cache = {}; },
    get: function (faceCss) {
      if (!this.ctx) this.ctx = document.createElement('canvas').getContext('2d');
      if (this.cache[faceCss]) return this.cache[faceCss];
      var ctx = this.ctx;
      ctx.font = '100px ' + faceCss + ', sans-serif';
      var mH = ctx.measureText('H');
      var mD = ctx.measureText('gjpqy');
      var cap = mH.actualBoundingBoxAscent ? mH.actualBoundingBoxAscent / 100 : 0.716;
      var desc = mD.actualBoundingBoxDescent ? mD.actualBoundingBoxDescent / 100 : 0.21;
      var info = { cap: cap, desc: desc, widths: {} };
      this.cache[faceCss] = info;
      return info;
    },
    width: function (faceCss, text) {
      var info = this.get(faceCss);
      if (info.widths[text] === undefined) {
        this.ctx.font = '100px ' + faceCss + ', sans-serif';
        info.widths[text] = this.ctx.measureText(text).width / 100;
      }
      return info.widths[text];
    }
  };

  /* ------------------------------------------------------------------ *
   * Builder
   * ------------------------------------------------------------------ */
  function Builder(root) {
    this.root = root;
    this.uid = 'fgb' + Math.random().toString(36).slice(2, 8);
    var cfgEl = root.querySelector('script[data-fgb-config]');
    this.cfg = JSON.parse(cfgEl.textContent);
    this.t = Object.assign({}, T, this.cfg.text || {});
    this.money = this.cfg.moneyFormat || '${{amount}}';
    this.steps = [];
    this.previews = [];
    this.cartSubtotal = 0;
    this.comboFailed = {};
    this.init();
  }

  Builder.prototype.init = function () {
    var c = this.cfg;
    var p = c.product;
    this.eng = Object.assign({
      maxChars: 120, maxLines: 7, areaW: 26, areaH: 15.5, plateW: 30, plateH: 20, faceOffset: -0.45,
      minCap: 1.0, goodCap: 1.5, maxFont: 6, lineHeight: 1.22, required: true,
      chips: '', placeholder: '', lineLabel: 'Line', fontLabel: 'Font'
    }, c.engraving || {});

    // Which product option is the band, which is the finish
    var opts = (p.options || []).map(norm);
    var bi = opts.indexOf(norm(p.bandOption));
    if (bi < 0) bi = opts.findIndex(function (o) { return /band|colou?r|strap/.test(o); });
    var fi = opts.indexOf(norm(p.finishOption));
    if (fi < 0) fi = opts.findIndex(function (o, i) { return i !== bi && /finish|plate|metal|clasp|tone/.test(o); });
    if (bi < 0) bi = 0;
    if (fi < 0) fi = bi === 0 ? 1 : 0;
    this.bi = bi; this.fi = fi;
    this.variants = p.variants || [];

    var self = this;
    var bandValues = [], finishValues = [];
    this.variants.forEach(function (v) {
      var b = v.options[bi], f = v.options[fi];
      if (b !== undefined && bandValues.indexOf(b) < 0) bandValues.push(b);
      if (f !== undefined && finishValues.indexOf(f) < 0) finishValues.push(f);
    });

    // Band swatches: textarea order first, then any remaining values
    var sw = parseSwatches(c.swatches);
    this.bands = [];
    sw.forEach(function (s) {
      var real = bandValues.find(function (v) { return norm(v) === norm(s.name); });
      if (real) self.bands.push({ name: real, hex: s.hex });
    });
    bandValues.forEach(function (v) {
      if (!self.bands.some(function (b) { return b.name === v; })) {
        self.bands.push({ name: v, hex: FALLBACK_COLOURS[norm(v)] || '#9a9ea4' });
      }
    });

    // Finishes: textarea order first, then remaining values
    var fs = parseFinishStyles(c.finishStyles);
    var order = Object.keys(fs);
    var assets = c.finishAssets || {};
    this.finishes = [];
    var sortedFinish = finishValues.slice().sort(function (a, b) {
      var ia = order.indexOf(norm(a)), ib = order.indexOf(norm(b));
      return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
    });
    sortedFinish.forEach(function (v) {
      var st = fs[norm(v)] || FALLBACK_FINISHES[norm(v)] || { light: '#e9eaec', dark: '#9da1a7', text: '#34373c' };
      var a = assets[v] || {};
      self.finishes.push({ name: v, light: st.light, dark: st.dark, text: st.text, plate: a.plate || '', charm: a.charm || '', plateOk: !!a.plate, charmOk: !!a.charm });
    });

    // Fonts
    this.fonts = parseFonts(c.fonts).map(function (f) {
      var face = FACES[f.key];
      return { label: f.label, key: f.key, engrave: f.engrave, css: face ? face.css : f.key, stack: face ? face.stack : f.key, face: face || {} };
    });
    if (!this.fonts.length) this.fonts = [{ label: 'Arial', key: 'arial', engrave: 'Arial', css: FACES.arial.css, stack: FACES.arial.stack, face: FACES.arial }];

    // Add-ons
    var ad = c.addons || {};
    this.addons = {
      charm: ad.charm && ad.charm.variants && ad.charm.variants.length ? ad.charm : null,
      band: ad.band && ad.band.variants && ad.band.variants.length ? ad.band : null,
      packaging: ad.packaging && ad.packaging.id ? ad.packaging : null,
      card: ad.card && ad.card.id ? ad.card : null
    };

    // Initial state from the variant in the URL / first available
    var init = this.variants.find(function (v) { return String(v.id) === String(p.initialVariant); }) || this.variants.find(function (v) { return v.available; }) || this.variants[0];
    this.state = {
      band: init ? init.options[bi] : (this.bands[0] && this.bands[0].name),
      finish: init ? init.options[fi] : (this.finishes[0] && this.finishes[0].name),
      charm: this.addons.charm ? null : false,
      text: '',
      font: 0,
      spares: [],
      packaging: false,
      card: false,
      confirmed: false,
      zoom: false,
      view: 'combo'
    };

    var cp = this.cfg.comboPhotos;
    this.combosOn = !!(cp && typeof cp === 'object' && Object.keys(cp).length);
    this.render();
    this.loadFonts();
    this.fetchCart();
    this.update();
    if (this.combosOn) this.showCombo(); else this.showPhoto(null);
    this.root.classList.add('is-ready');
  };

  /* ---------------- data lookups ---------------- */
  Builder.prototype.variantFor = function (band, finish) {
    var bi = this.bi, fi = this.fi;
    return this.variants.find(function (v) { return v.options[bi] === band && v.options[fi] === finish; }) || null;
  };
  Builder.prototype.variant = function () { return this.variantFor(this.state.band, this.state.finish); };
  Builder.prototype.finishObj = function (name) {
    name = name || this.state.finish;
    return this.finishes.find(function (f) { return f.name === name; }) || this.finishes[0];
  };
  Builder.prototype.bandObj = function (name) {
    name = name || this.state.band;
    return this.bands.find(function (b) { return b.name === name; }) || this.bands[0];
  };
  Builder.prototype.addonVariant = function (addon, value) {
    if (!addon) return null;
    var v = norm(value);
    return addon.variants.find(function (x) {
      return norm(x.name) === v || (x.options || []).some(function (o) { return norm(o) === v; });
    }) || null;
  };
  Builder.prototype.charmVariant = function () { return this.addonVariant(this.addons.charm, this.state.finish); };
  Builder.prototype.fontObj = function () { return this.fonts[this.state.font] || this.fonts[0]; };
  Builder.prototype.$ = function (sel) { return this.root.querySelector(sel); };

  /* ---------------- engraving ---------------- */
  Builder.prototype.engravingLines = function () {
    return String(this.state.text || '').split('\n').map(function (l) { return l.trim(); }).filter(Boolean);
  };

  Builder.prototype.fit = function (lineArr, font) {
    var e = this.eng;
    var css = font.css;
    var m = Measure.get(css);
    var n = lineArr.length;
    if (!n) return null;
    var maxW = 0;
    lineArr.forEach(function (l) { maxW = Math.max(maxW, Measure.width(css, l)); });
    var fsW = maxW > 0 ? e.areaW / maxW : e.maxFont;
    var fsH = e.areaH / ((n - 1) * e.lineHeight + m.cap + m.desc * 0.75);
    var fs = Math.min(fsW, fsH, e.maxFont);
    var cap = fs * m.cap;
    var c1 = Math.round(cap * 10) / 10;          // judge on the value we display
    var level = c1 >= e.goodCap ? 'great' : c1 >= (e.minCap + e.goodCap) / 2 ? 'good' : c1 >= e.minCap ? 'small' : 'tiny';
    return { fs: fs, cap: cap, capRatio: m.cap, lh: e.lineHeight, level: level, n: n };
  };

  Builder.prototype.sanitize = function (value) {
    var e = this.eng, flags = {};
    var v = String(value || '').replace(/\r\n?/g, '\n').replace(/\t/g, ' ');
    var noEmoji = v.replace(PICTO, '');
    if (noEmoji !== v) flags.emoji = true;
    v = noEmoji;
    var ls = v.split('\n');
    if (ls.length > e.maxLines) {
      var keep = ls.slice(0, e.maxLines - 1);
      keep.push(ls.slice(e.maxLines - 1).join(' '));
      ls = keep;
      flags.lines = true;
    }
    v = ls.join('\n');
    var count = 0, out = '';
    var arr = Array.from(v);
    for (var i = 0; i < arr.length; i++) {
      var ch = arr[i];
      if (ch === '\n') { out += ch; continue; }
      if (count >= e.maxChars) { flags.chars = true; continue; }
      out += ch; count++;
    }
    return { value: out, flags: flags };
  };

  /* ------------------------------------------------------------------ *
   * Rendering
   * ------------------------------------------------------------------ */
  Builder.prototype.render = function () {
    var self = this;
    var t = this.t;

    // ---- Media: photo of the combination on top, live engraving preview underneath ----
    this.shot = this.$('[data-fgb-shot]');
    this.shotCombo = this.$('[data-fgb-combo]');
    this.shotPhoto = this.$('[data-fgb-photo]');
    this.stage = this.$('[data-fgb-stage]');
    this.stagePreview = this.$('[data-fgb-preview]');
    if (this.shot && this.combosOn && !this.shotCombo) {
      this.shotCombo = h('img', { class: 'fgb-shot__combo', alt: '', width: 1200, height: 900 });
      this.shot.insertBefore(this.shotCombo, this.shot.firstChild);
    }
    if (this.shotCombo) {
      this.shotCombo.addEventListener('error', function () {
        var src = self.shotCombo.getAttribute('src');
        if (src) self.comboFailed[src] = true;
        self.updateComboThumb();
        if (self.state.view === 'combo') self.showCombo();
      });
      // The first photo may already have failed before this script ran
      var firstSrc = this.shotCombo.getAttribute('src');
      if (firstSrc && this.shotCombo.complete && !this.shotCombo.naturalWidth) this.comboFailed[firstSrc] = true;
    }
    if (this.shot) {
      this.backBtn = h('button', { type: 'button', class: 'fgb-shot__back', onclick: function () { self.showCombo(); } }, [this.icon('back'), h('span', { text: t.backToDesign })]);
      this.backBtn.hidden = true;
      this.caption = h('div', { class: 'fgb-shot__caption', 'aria-live': 'polite' });
      append(this.shot, [this.backBtn, this.caption]);
    }
    if (this.stagePreview) {
      this.previews.push(new Preview(this, this.stagePreview, { suffix: 'main' }));
      this.zoomBtn = h('button', { type: 'button', class: 'fgb-stage__zoom', 'aria-pressed': 'false', 'aria-label': t.zoomIn, title: t.zoomIn, onclick: function () { self.toggleZoom(); } }, [this.icon('zoom')]);
      this.liveTag = h('span', { class: 'fgb-stage__tag' }, [h('i', { 'aria-hidden': 'true' }), h('span', { text: t.livePreview })]);
      this.exampleTag = h('span', { class: 'fgb-stage__example', text: t.example + ' text' });
      append(this.stage, [this.liveTag, this.zoomBtn, this.exampleTag]);
    }

    // thumbnails: your bracelet first, then the gallery photos
    this.comboThumb = this.$('[data-fgb-thumb="combo"]');
    if (!this.comboThumb && this.combosOn) {
      var row = this.$('[data-fgb-thumbs]');
      if (row) {
        this.comboThumb = h('button', { type: 'button', class: 'fgb-thumb fgb-thumb--combo', 'data-fgb-thumb': 'combo', 'aria-pressed': 'true', 'aria-label': t.yourDesign }, [
          h('img', { alt: '', width: 72, height: 72 }),
          h('span', { class: 'fgb-thumb__label', text: t.viewBracelet })
        ]);
        row.insertBefore(this.comboThumb, row.firstChild);
      }
    }
    this.comboThumbImg = this.comboThumb ? this.comboThumb.querySelector('img') : null;
    if (this.comboThumbImg) {
      this.comboThumbImg.addEventListener('load', function () { self.comboThumb.hidden = false; });
      this.comboThumbImg.addEventListener('error', function () { self.comboThumb.hidden = true; });
    }
    this.$all('[data-fgb-thumb]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (btn.getAttribute('data-fgb-thumb') === 'combo') self.showCombo();
        else self.showPhoto(btn);
      });
    });

    // ---- Steps ----
    var mount = this.$('[data-fgb-steps]');
    if (!mount) return;
    mount.innerHTML = '';
    this._stepsMount = mount;
    this.buildBand(mount);
    this.buildFinish(mount);
    if (this.addons.charm) this.buildCharm(mount);
    this.buildEngraving(mount);
    if (this.fonts.length > 1) this.buildFont(mount);
    if (this.addons.band || this.addons.packaging || this.addons.card) this.buildExtras(mount);
    this.buildSummary(mount);
    this.buildBar();
    this.buildPeek();

    // Number visible steps
    var n = 0;
    this.steps.forEach(function (s) { n++; s.index = n; s.num.textContent = n; });
  };

  Builder.prototype.$all = function (sel) { return Array.prototype.slice.call(this.root.querySelectorAll(sel)); };

  Builder.prototype.icon = function (name) {
    var paths = {
      zoom: 'M10.5 4a6.5 6.5 0 1 0 4.06 11.58l4.43 4.43 1.41-1.41-4.43-4.43A6.5 6.5 0 0 0 10.5 4Zm0 2a4.5 4.5 0 1 1 0 9 4.5 4.5 0 0 1 0-9Zm-1 1.75v2h-2v1.5h2v2h1.5v-2h2v-1.5h-2v-2h-1.5Z',
      unzoom: 'M10.5 4a6.5 6.5 0 1 0 4.06 11.58l4.43 4.43 1.41-1.41-4.43-4.43A6.5 6.5 0 0 0 10.5 4Zm0 2a4.5 4.5 0 1 1 0 9 4.5 4.5 0 0 1 0-9Zm-3 3.75v1.5h6v-1.5h-6Z',
      back: 'M11.7 5.3 5 12l6.7 6.7 1.4-1.4L8.8 13H19v-2H8.8l4.3-4.3-1.4-1.4Z',
      check: 'M9.5 16.2 5.3 12l-1.4 1.4 5.6 5.6L20.1 8.4 18.7 7l-9.2 9.2Z'
    };
    return sv('svg', { viewBox: '0 0 24 24', width: 20, height: 20, 'aria-hidden': 'true', focusable: 'false' }, [sv('path', { d: paths[name], fill: 'currentColor' })]);
  };

  Builder.prototype.step = function (key, title, opts) {
    opts = opts || {};
    var num = h('span', { class: 'fgb-step__num', 'aria-hidden': 'true' });
    var value = h('span', { class: 'fgb-step__value' });
    var head = h('div', { class: 'fgb-step__head' }, [num, h('h3', { class: 'fgb-step__title', text: title }), value]);
    var body = h('div', { class: 'fgb-step__body' });
    var msg = h('p', { class: 'fgb-step__msg', role: 'alert' });
    msg.hidden = true;
    var sec = h('section', { class: 'fgb-step', 'data-step': key, 'aria-label': title }, [head, body, msg]);
    var rec = { key: key, el: sec, num: num, value: value, body: body, msg: msg, optional: !!opts.optional };
    this.steps.push(rec);
    this._stepsMount.appendChild(sec);
    return rec;
  };

  /* ---- 1. Band ---- */
  Builder.prototype.buildBand = function (mount) {
    var self = this, t = this.t;
    var st = this.step('band', t.bandTitle);
    var group = h('div', { class: 'fgb-bands', role: 'radiogroup', 'aria-label': t.bandTitle });
    this.bandInputs = {};
    this.bands.forEach(function (b, i) {
      var id = self.uid + '-band-' + i;
      var input = h('input', { type: 'radio', class: 'fgb-vh', id: id, name: self.uid + '-band', value: b.name });
      input.addEventListener('change', function () { self.set({ band: b.name }); });
      var light = luminance(b.hex) > 0.6;
      var lab = h('label', { class: 'fgb-band' + (light ? ' is-light' : ''), for: id }, [
        h('span', { class: 'fgb-band__chip', style: { '--c': b.hex } }),
        h('span', { class: 'fgb-band__name', text: b.name })
      ]);
      self.bandInputs[b.name] = { input: input, label: lab };
      group.appendChild(input);
      group.appendChild(lab);
    });
    st.body.appendChild(group);
  };

  /* ---- 2. Finish ---- */
  Builder.prototype.buildFinish = function () {
    var self = this, t = this.t;
    var st = this.step('finish', t.finishTitle);
    var group = h('div', { class: 'fgb-finishes', role: 'radiogroup', 'aria-label': t.finishTitle });
    this.finishInputs = {};
    this.finishes.forEach(function (f, i) {
      var id = self.uid + '-fin-' + i;
      var input = h('input', { type: 'radio', class: 'fgb-vh', id: id, name: self.uid + '-finish', value: f.name });
      input.addEventListener('change', function () { self.set({ finish: f.name }); });
      var media = h('span', { class: 'fgb-finish__media', style: { '--l': f.light, '--d': f.dark } });
      if (f.plate) {
        var img = h('img', { class: 'fgb-finish__img', src: f.plate, alt: '', width: 156, height: 107, decoding: 'async' });
        img.addEventListener('error', function () { if (img.parentNode) img.parentNode.removeChild(img); media.classList.add('is-drawn'); });
        media.appendChild(img);
      } else {
        media.classList.add('is-drawn');
      }
      var price = h('span', { class: 'fgb-finish__price' });
      var lab = h('label', { class: 'fgb-finish', for: id }, [media, h('span', { class: 'fgb-finish__name', text: f.name }), price]);
      self.finishInputs[f.name] = { input: input, label: lab, price: price };
      group.appendChild(input);
      group.appendChild(lab);
    });
    st.body.appendChild(group);
    st.body.appendChild(h('p', { class: 'fgb-help', text: t.finishHelp }));
  };

  /* ---- 3. Charm ---- */
  Builder.prototype.buildCharm = function () {
    var self = this, t = this.t, a = this.addons.charm;
    var st = this.step('charm', a.title || t.charmTitle);
    var group = h('div', { class: 'fgb-choices', role: 'radiogroup', 'aria-label': a.title || t.charmTitle });
    var mk = function (val, title, meta, withImg, badge) {
      var id = self.uid + '-charm-' + val;
      var input = h('input', { type: 'radio', class: 'fgb-vh', id: id, name: self.uid + '-charm', value: val });
      input.addEventListener('change', function () { self.set({ charm: val === 'yes' }); });
      var media = h('span', { class: 'fgb-choice__media' + (withImg ? '' : ' is-empty') });
      var price = h('span', { class: 'fgb-choice__price' });
      var lab = h('label', { class: 'fgb-choice', for: id }, [
        media,
        h('span', { class: 'fgb-choice__body' }, [
          h('span', { class: 'fgb-choice__title' }, [title, badge ? h('span', { class: 'fgb-badge', text: badge }) : null]),
          h('span', { class: 'fgb-choice__meta', text: meta }),
          price
        ])
      ]);
      group.appendChild(input);
      group.appendChild(lab);
      return { input: input, label: lab, media: media, price: price };
    };
    this.charmYes = mk('yes', t.charmAdd, a.text || t.charmAddMeta, true, t.charmBadge);
    this.charmNo = mk('no', t.charmNone, t.charmNoneMeta, false, null);
    this.charmNo.media.appendChild(h('span', { class: 'fgb-choice__noimg', 'aria-hidden': 'true' }));
    st.body.appendChild(group);
    st.body.appendChild(h('p', { class: 'fgb-help', text: t.charmHelp }));
  };

  /* ---- 4. Engraving ---- */
  Builder.prototype.buildEngraving = function () {
    var self = this, t = this.t, e = this.eng;
    var st = this.step('engraving', t.engraveTitle);
    var id = this.uid + '-text';
    var ta = h('textarea', {
      id: id, class: 'fgb-textarea', rows: 5, autocomplete: 'off', autocapitalize: 'sentences', spellcheck: 'true',
      'aria-describedby': id + '-meta'
    });
    ta.placeholder = e.placeholder || '';
    this.textarea = ta;
    var count = h('span', { class: 'fgb-count' });
    var lineCount = h('span', { class: 'fgb-count' });
    var meta = h('div', { class: 'fgb-engrave__meta', id: id + '-meta' }, [count, lineCount]);
    this.countEl = count; this.lineEl = lineCount;

    // size meter
    this.sizeBar = h('i', { class: 'fgb-size__fill' });
    this.sizeVal = h('span', { class: 'fgb-size__value' });
    this.sizeHint = h('p', { class: 'fgb-size__hint' });
    this.sizeWrap = h('div', { class: 'fgb-size', 'aria-live': 'polite' }, [
      h('span', { class: 'fgb-size__label', text: t.sizeLabel }),
      h('span', { class: 'fgb-size__track' }, [this.sizeBar]),
      this.sizeVal,
      this.sizeHint
    ]);

    // quick add chips
    var chipList = lines(e.chips);
    var chips = null;
    if (chipList.length) {
      chips = h('div', { class: 'fgb-chips' }, [h('span', { class: 'fgb-chips__label', text: t.quickAdd })]);
      chipList.forEach(function (c) {
        chips.appendChild(h('button', { type: 'button', class: 'fgb-chip', text: c, onclick: function () { self.insertPhrase(c); } }));
      });
    }

    // inline preview (mobile)
    var inline = h('div', { class: 'fgb-inline-preview', 'aria-hidden': 'true' });
    this.flash = h('p', { class: 'fgb-flash', 'aria-live': 'polite' });
    this.flash.hidden = true;

    st.body.appendChild(h('label', { class: 'fgb-label', for: id, text: t.engraveLabel }));
    st.body.appendChild(ta);
    st.body.appendChild(meta);
    st.body.appendChild(this.flash);
    st.body.appendChild(inline);
    st.body.appendChild(this.sizeWrap);
    if (chips) st.body.appendChild(chips);
    st.body.appendChild(h('p', { class: 'fgb-help fgb-preview-note', text: t.previewNote }));
    this.previews.push(new Preview(this, inline, { suffix: 'inl', fixedZoom: true }));

    ta.addEventListener('keydown', function (ev) {
      if (ev.key === 'Enter' && !ev.isComposing) {
        var sel = ta.value.slice(ta.selectionStart, ta.selectionEnd);
        if (ta.value.split('\n').length >= e.maxLines && sel.indexOf('\n') < 0) {
          ev.preventDefault();
          self.showFlash(fmt(t.maxLines, { n: e.maxLines }));
        }
      }
    });
    ta.addEventListener('input', function () { self.onText(); });
  };

  Builder.prototype.onText = function () {
    var ta = this.textarea, t = this.t;
    var caret = ta.selectionStart;
    var res = this.sanitize(ta.value);
    if (res.value !== ta.value) {
      var before = this.sanitize(ta.value.slice(0, caret)).value.length;
      ta.value = res.value;
      try { ta.setSelectionRange(before, before); } catch (e) { /* ignore */ }
      if (res.flags.emoji) this.showFlash(t.emojiRemoved);
      else if (res.flags.chars) this.showFlash(fmt(t.maxChars, { n: this.eng.maxChars }));
      else if (res.flags.lines) this.showFlash(fmt(t.maxLines, { n: this.eng.maxLines }));
    }
    this.set({ text: ta.value });
  };

  Builder.prototype.insertPhrase = function (phrase) {
    var ta = this.textarea;
    var add = phrase;
    if (/[:]$|\bto$/i.test(add)) add += ' ';
    var v = ta.value;
    var start = ta.selectionStart, end = ta.selectionEnd;
    if (document.activeElement !== ta) { start = end = v.length; }
    var lineStart = v.lastIndexOf('\n', start - 1) + 1;
    var before = v.slice(lineStart, start);
    var lineCount = v.split('\n').length;
    if (before.trim().length) {
      add = (lineCount < this.eng.maxLines ? '\n' : ' ') + add;
    }
    ta.value = v.slice(0, start) + add + v.slice(end);
    var pos = start + add.length;
    ta.focus();
    try { ta.setSelectionRange(pos, pos); } catch (e) { /* ignore */ }
    this.onText();
  };

  Builder.prototype.showFlash = function (msg) {
    var f = this.flash;
    f.textContent = msg;
    f.hidden = false;
    clearTimeout(this._flashT);
    this._flashT = setTimeout(function () { f.hidden = true; }, 3200);
  };

  /* ---- 5. Font ---- */
  Builder.prototype.buildFont = function () {
    var self = this, t = this.t;
    var st = this.step('font', t.fontTitle);
    var group = h('div', { class: 'fgb-fonts', role: 'radiogroup', 'aria-label': t.fontTitle });
    this.fontInputs = [];
    this.fonts.forEach(function (f, i) {
      var id = self.uid + '-font-' + i;
      var input = h('input', { type: 'radio', class: 'fgb-vh', id: id, name: self.uid + '-font', value: i });
      input.addEventListener('change', function () { self.set({ font: i }); });
      var sample = h('span', { class: 'fgb-font__sample', style: { 'font-family': f.stack } });
      var lab = h('label', { class: 'fgb-font', for: id }, [sample, h('span', { class: 'fgb-font__name', text: f.label })]);
      self.fontInputs.push({ input: input, label: lab, sample: sample });
      group.appendChild(input);
      group.appendChild(lab);
    });
    this.fontNote = h('p', { class: 'fgb-help fgb-help--note' });
    this.fontNote.hidden = true;
    st.body.appendChild(group);
    st.body.appendChild(this.fontNote);
  };

  /* ---- 6. Extras ---- */
  Builder.prototype.buildExtras = function () {
    var self = this, t = this.t, a = this.addons;
    var st = this.step('extras', t.extrasTitle, { optional: true });
    st.value.textContent = t.optional;

    if (a.band) {
      var box = h('div', { class: 'fgb-extra fgb-extra--bands' });
      var price = h('span', { class: 'fgb-extra__price' });
      this.spareSel = h('p', { class: 'fgb-extra__sel', 'aria-live': 'polite' });
      var group = h('div', { class: 'fgb-bands fgb-bands--sm', role: 'group', 'aria-label': a.band.title || t.spareTitle });
      this.spareInputs = {};
      this.bands.forEach(function (b, i) {
        var v = self.addonVariant(a.band, b.name);
        if (!v) return;
        var id = self.uid + '-spare-' + i;
        var input = h('input', { type: 'checkbox', class: 'fgb-vh', id: id, value: b.name });
        input.addEventListener('change', function () { self.toggleSpare(b.name, input.checked, input); });
        var light = luminance(b.hex) > 0.6;
        var lab = h('label', { class: 'fgb-band fgb-band--multi' + (light ? ' is-light' : ''), for: id }, [
          h('span', { class: 'fgb-band__chip', style: { '--c': b.hex } }, [self.icon('check')]),
          h('span', { class: 'fgb-band__name', text: b.name })
        ]);
        if (!v.available) { input.disabled = true; lab.classList.add('is-unavailable'); }
        self.spareInputs[b.name] = { input: input, label: lab, variant: v };
        group.appendChild(input);
        group.appendChild(lab);
      });
      append(box, [
        h('div', { class: 'fgb-extra__head' }, [h('span', { class: 'fgb-extra__title', text: a.band.title || t.spareTitle }), price]),
        h('p', { class: 'fgb-extra__text', text: a.band.text || t.spareText }),
        group,
        this.spareSel
      ]);
      this.sparePrice = price;
      st.body.appendChild(box);
    }

    var toggle = function (key, addon, img) {
      var id = self.uid + '-' + key;
      var input = h('input', { type: 'checkbox', class: 'fgb-vh', id: id });
      input.addEventListener('change', function () { var o = {}; o[key] = input.checked; self.set(o); });
      var priceEl = h('span', { class: 'fgb-extra__price', text: '+' + formatMoney(addon.price, self.money) });
      var lab = h('label', { class: 'fgb-extra fgb-extra--toggle', for: id }, [
        img ? h('span', { class: 'fgb-extra__img' }, [h('img', { src: img, alt: '', loading: 'lazy', width: 64, height: 64 })]) : null,
        h('span', { class: 'fgb-extra__body' }, [
          h('span', { class: 'fgb-extra__head' }, [h('span', { class: 'fgb-extra__title', text: addon.title }), priceEl]),
          addon.text ? h('span', { class: 'fgb-extra__text', text: addon.text }) : null
        ]),
        h('span', { class: 'fgb-switch', 'aria-hidden': 'true' }, [h('i')])
      ]);
      if (!addon.available) { input.disabled = true; lab.classList.add('is-unavailable'); }
      st.body.appendChild(input);
      st.body.appendChild(lab);
      return input;
    };
    if (a.packaging) this.packInput = toggle('packaging', a.packaging, a.packaging.image);
    if (a.card) this.cardInput = toggle('card', a.card, a.card.image);
  };

  Builder.prototype.toggleSpare = function (name, on, input) {
    var max = (this.addons.band && this.addons.band.max) || 5;
    var list = this.state.spares.slice();
    if (on) {
      if (list.length >= max) {
        input.checked = false;
        this.spareSel.textContent = fmt(this.t.spareMax, { n: max });
        return;
      }
      list.push(name);
    } else {
      list = list.filter(function (x) { return x !== name; });
    }
    this.set({ spares: list });
  };

  /* ---- Summary + add to cart ---- */
  Builder.prototype.buildSummary = function (mount) {
    var self = this, t = this.t, c = this.cfg;
    this.sumList = h('ul', { class: 'fgb-summary__lines' });
    this.sumTotal = h('span', { class: 'fgb-summary__amount' });
    this.shipEl = h('p', { class: 'fgb-ship' });
    this.shipEl.hidden = true;
    this.atc = h('button', { type: 'button', class: 'fgb-atc', onclick: function () { self.addToCart(); } }, [h('span', { class: 'fgb-atc__label', text: t.addToCart }), h('span', { class: 'fgb-atc__price' })]);
    this.atcMsg = h('p', { class: 'fgb-atc-msg', role: 'alert' });
    this.atcMsg.hidden = true;
    var kids = [
      h('h3', { class: 'fgb-summary__title', text: t.summaryTitle }),
      this.sumList,
      h('div', { class: 'fgb-summary__total' }, [h('span', { text: t.total }), this.sumTotal]),
      this.shipEl
    ];
    if (c.confirm && c.confirm.enabled) {
      var cid = this.uid + '-confirm';
      this.confirmInput = h('input', { type: 'checkbox', id: cid, class: 'fgb-check__input' });
      this.confirmInput.addEventListener('change', function () { self.set({ confirmed: self.confirmInput.checked }); });
      this.confirmWrap = h('label', { class: 'fgb-check', for: cid }, [this.confirmInput, h('span', { class: 'fgb-check__box', 'aria-hidden': 'true' }, [this.icon('check')]), h('span', { class: 'fgb-check__text', text: c.confirm.text })]);
      kids.push(this.confirmWrap);
    }
    kids.push(this.atc, this.atcMsg);
    this.summary = h('div', { class: 'fgb-summary' }, kids);
    mount.appendChild(this.summary);
  };

  Builder.prototype.buildBar = function () {
    var self = this, t = this.t;
    this.barTotal = h('span', { class: 'fgb-bar__total' });
    this.bar = h('div', { class: 'fgb-bar', 'aria-hidden': 'true' }, [
      h('div', { class: 'fgb-bar__info' }, [h('span', { class: 'fgb-bar__label', text: t.total }), this.barTotal]),
      h('button', { type: 'button', class: 'fgb-bar__btn', tabindex: '-1', text: t.addToCart, onclick: function () { self.addToCart(); } })
    ]);
    this.root.appendChild(this.bar);
    var steps = this.$('[data-fgb-steps]');
    var ticking = false;
    var sync = function () {
      ticking = false;
      var vh = window.innerHeight || document.documentElement.clientHeight;
      var top = steps.getBoundingClientRect().top;
      var sum = self.atc.getBoundingClientRect();
      var barH = self.bar.offsetHeight || 70;
      var atcVisible = sum.bottom > 0 && sum.top < vh - barH;
      var show = top < vh * 0.35 && !atcVisible && sum.top > 0;
      self.bar.classList.toggle('is-shown', show);
    };
    var onScroll = function () { if (!ticking) { ticking = true; requestAnimationFrame(sync); } };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    sync();
  };


  // Mobile: a small floating photo of their combination, so band, finish and charm changes
  // are visible while the big photo is scrolled off screen. Tap to jump back to the photo.
  Builder.prototype.buildPeek = function () {
    var self = this, t = this.t;
    if (!this.combosOn || !this.shot) return;
    this.peekImg = h('img', { class: 'fgb-peek__img', alt: '', width: 240, height: 180 });
    this.peekText = h('span', { class: 'fgb-peek__text' });
    this.peek = h('button', { type: 'button', class: 'fgb-peek', 'aria-label': t.peekTap, onclick: function () {
      var top = self.shot.getBoundingClientRect().top + (window.pageYOffset || document.documentElement.scrollTop) - (self.cfg.scrollOffset || 80);
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    } }, [this.peekImg, h('span', { class: 'fgb-peek__label' }, [h('span', { class: 'fgb-peek__kicker', text: t.peekLabel }), this.peekText])]);
    this.root.appendChild(this.peek);
    var steps = this.$('[data-fgb-steps]');
    var engrave = this.$('.fgb-inline-preview');
    var ticking = false;
    var sync = function () {
      ticking = false;
      var vh = window.innerHeight || document.documentElement.clientHeight;
      var shot = self.shot.getBoundingClientRect();
      var st = steps ? steps.getBoundingClientRect() : { top: 0, bottom: vh };
      var sum = self.summary ? self.summary.getBoundingClientRect() : { top: vh * 2 };
      var eng = engrave ? engrave.getBoundingClientRect() : null;
      var engVisible = eng && eng.height > 0 && eng.bottom > 60 && eng.top < vh - 60;
      var show = shot.bottom < 60 && st.top < vh * 0.6 && sum.top > vh * 0.45 && !engVisible && !!self.peekImg.getAttribute('src');
      self.peek.classList.toggle('is-shown', show);
      self.peek.classList.toggle('is-raised', !!(self.bar && self.bar.classList.contains('is-shown')));
    };
    this.peekSync = function () { if (!ticking) { ticking = true; requestAnimationFrame(sync); } };
    window.addEventListener('scroll', this.peekSync, { passive: true });
    window.addEventListener('resize', this.peekSync, { passive: true });
  };

  Builder.prototype.updatePeek = function (changed) {
    if (!this.peek) return;
    var url = this.comboUrl();
    if (url && this.peekImg.getAttribute('src') !== url) this.peekImg.setAttribute('src', url);
    if (!url) this.peekImg.removeAttribute('src');
    var s = this.state;
    this.peekText.textContent = s.band + ' \u00b7 ' + s.finish + (s.charm === false ? '' : ' \u00b7 charm');
    if (changed) {
      var el = this.peek;
      el.classList.remove('is-pulse');
      void el.offsetWidth;          // restart the animation
      el.classList.add('is-pulse');
    }
    this.peekSync();
  };

  /* ------------------------------------------------------------------ *
   * State + updates
   * ------------------------------------------------------------------ */
  Builder.prototype.set = function (patch) {
    var s = this.state;
    var comboChanged = false;
    Object.keys(patch).forEach(function (k) {
      if (s[k] !== patch[k] && (k === 'band' || k === 'finish' || k === 'charm')) comboChanged = true;
      s[k] = patch[k];
    });
    // A new band, finish or charm choice brings the photo back to their combination
    if (comboChanged && this.combosOn) this.showCombo();
    this.peekChanged = this.peekChanged || comboChanged;
    if (patch.band !== undefined || patch.finish !== undefined) this.syncUrl();
    if (patch.font !== undefined) this.loadFont(this.fontObj());
    this.clearErrors(Object.keys(patch));
    this.update();
  };

  Builder.prototype.update = function () {
    var self = this, s = this.state, t = this.t;
    var v = this.variant();
    var fin = this.finishObj();
    var band = this.bandObj();
    var font = this.fontObj();
    var engr = this.engravingLines();
    var fit = this.fit(engr, font);
    this.lastFit = fit;

    // previews
    this.previews.forEach(function (p) { p.update(); });

    // band swatches + availability against current finish
    Object.keys(this.bandInputs).forEach(function (name) {
      var o = self.bandInputs[name];
      o.input.checked = name === s.band;
      var vv = self.variantFor(name, s.finish);
      o.label.classList.toggle('is-unavailable', !vv || !vv.available);
    });
    Object.keys(this.finishInputs).forEach(function (name) {
      var o = self.finishInputs[name];
      o.input.checked = name === s.finish;
      var vv = self.variantFor(s.band, name);
      o.label.classList.toggle('is-unavailable', !vv || !vv.available);
      o.price.textContent = vv ? formatMoney(vv.price, self.money) : '';
    });

    // charm
    if (this.charmYes) {
      var cv = this.charmVariant();
      this.charmYes.input.checked = s.charm === true;
      this.charmNo.input.checked = s.charm === false;
      this.charmYes.price.textContent = cv ? '+' + formatMoney(cv.price, this.money) : '';
      var unavailable = !cv || !cv.available;
      this.charmYes.input.disabled = unavailable;
      this.charmYes.label.classList.toggle('is-unavailable', unavailable);
      if (unavailable && s.charm === true) s.charm = null;
      var cm = this.charmYes.media;
      if (fin && fin.charm && fin.charmOk && this.imgFailed(fin.charm) === false) {
        cm.style.backgroundImage = 'url("' + fin.charm + '")';
        cm.classList.remove('is-drawn');
      } else {
        cm.style.backgroundImage = '';
        cm.classList.add('is-drawn');
        cm.style.setProperty('--l', fin ? fin.light : '#eee');
        cm.style.setProperty('--d', fin ? fin.dark : '#999');
      }
    }

    // font cards
    if (this.fontInputs) {
      var sampleText = engr[0] || lines(this.eng.placeholder)[0] || 'Aa Bb 123';
      if (sampleText.length > 18) sampleText = sampleText.slice(0, 17) + '\u2026';
      this.fontInputs.forEach(function (o, i) {
        var f = self.fonts[i];
        o.input.checked = i === s.font;
        o.sample.textContent = sampleText;
        o.sample.classList.toggle('is-twin', !!(f.face.twinStroke && !f.face._localOk));
      });
      var showTwin = !!(font.face.twinNote && !font.face._localOk);
      this.fontNote.hidden = !showTwin;
      if (showTwin) this.fontNote.textContent = fmt(t.twinNote, {});
    }

    // spares
    if (this.spareInputs) {
      Object.keys(this.spareInputs).forEach(function (name) {
        self.spareInputs[name].input.checked = s.spares.indexOf(name) >= 0;
      });
      var anyV = this.addons.band.variants[0];
      this.sparePrice.textContent = anyV ? '+' + formatMoney(anyV.price, this.money) + ' ' + t.each : '';
      if (s.spares.length) this.spareSel.textContent = fmt(t.spareSelected, { n: s.spares.length, list: s.spares.join(', ') });
      else this.spareSel.textContent = t.spareNone;
    }
    if (this.packInput) this.packInput.checked = !!s.packaging;
    if (this.cardInput) this.cardInput.checked = !!s.card;

    // engraving meta + size meter
    var e = this.eng;
    this.countEl.textContent = fmt(t.charsCount, { n: chars(s.text), max: e.maxChars });
    this.lineEl.textContent = fmt(t.linesCount, { n: engr.length, max: e.maxLines });
    this.countEl.classList.toggle('is-max', chars(s.text) >= e.maxChars);
    if (fit) {
      this.sizeWrap.hidden = false;
      var pct = clamp((fit.cap - 0.6) / (2.6 - 0.6), 0.04, 1) * 100;
      this.sizeBar.style.width = pct + '%';
      this.sizeWrap.setAttribute('data-level', fit.level);
      var label = { great: t.sizeGreat, good: t.sizeGood, small: t.sizeSmall, tiny: t.sizeTooSmall }[fit.level];
      this.sizeVal.textContent = fit.cap.toFixed(1) + ' mm \u00b7 ' + label;
      this.sizeHint.textContent = fit.level === 'tiny' ? t.tooSmallHelp : fit.level === 'small' ? t.smallHelp : '';
      this.sizeHint.hidden = !this.sizeHint.textContent;
    } else {
      this.sizeWrap.hidden = true;
    }

    // step header values + completion
    this.stepState();

    // prices
    var total = this.total();
    var totalStr = formatMoney(total, this.money);
    this.$all('[data-fgb-price]').forEach(function (n) { n.textContent = totalStr; });
    var cmp = this.$('[data-fgb-compare]');
    if (cmp) {
      var hasCmp = v && v.compare && v.compare > v.price;
      cmp.hidden = !hasCmp;
      if (hasCmp) cmp.textContent = formatMoney(total + (v.compare - v.price), this.money);
    }
    this.sumTotal.textContent = totalStr;
    this.barTotal.textContent = totalStr;
    this.atc.querySelector('.fgb-atc__price').textContent = totalStr;
    var soldOut = !v || !v.available;
    this.atc.classList.toggle('is-soldout', soldOut);
    this.atc.querySelector('.fgb-atc__label').textContent = soldOut ? t.soldOut : t.addToCart;
    this.renderSummary();
    this.renderShipping(total);
    this.syncAfterpay(total);

    // photo caption, example-text tag, combination thumb
    if (this.caption) {
      var bits = [band ? band.name + ' ' + t.band : '', fin ? fin.name + ' ' + t.finish : ''];
      if (s.charm === true) bits.push(t.charmChip);
      this.caption.innerHTML = '';
      bits.filter(Boolean).forEach(function (b) { self.caption.appendChild(h('span', { text: b })); });
    }
    if (this.exampleTag) this.exampleTag.hidden = engr.length > 0;
    this.updateComboThumb();
    this.updatePeek(this.peekChanged); this.peekChanged = false;
    if (s.view === 'combo' && !this.applyCombo()) this.showPhoto(null);
  };

  // Shared photo state for all previews: undefined (unknown), false (ok/loading), true (failed)
  Builder.prototype.imgFailed = function (url) {
    var st = this.previews.length ? this.previews[0].imgState[url] : undefined;
    return st === 'error';
  };

  Builder.prototype.total = function () {
    var s = this.state, a = this.addons, self = this;
    var v = this.variant();
    var total = v ? v.price : (this.variants[0] ? this.variants[0].price : 0);
    if (s.charm === true) { var cv = this.charmVariant(); if (cv) total += cv.price; }
    s.spares.forEach(function (name) { var bv = self.addonVariant(a.band, name); if (bv) total += bv.price; });
    if (s.packaging && a.packaging) total += a.packaging.price;
    if (s.card && a.card) total += a.card.price;
    return total;
  };

  Builder.prototype.renderSummary = function () {
    var s = this.state, a = this.addons, t = this.t, self = this;
    var v = this.variant();
    var ul = this.sumList;
    ul.innerHTML = '';
    var row = function (label, sub, cents) {
      ul.appendChild(h('li', {}, [
        h('span', { class: 'fgb-summary__item' }, [label, sub ? h('small', { text: sub }) : null]),
        h('span', { class: 'fgb-summary__price', text: formatMoney(cents, self.money) })
      ]));
    };
    var engr = this.engravingLines();
    row(t.bracelet + ' \u00b7 ' + s.band + ' / ' + s.finish, engr.length ? '\u201c' + engr.join(' / ') + '\u201d \u00b7 ' + this.fontObj().label : '', v ? v.price : 0);
    if (s.charm === true) { var cv = this.charmVariant(); if (cv) row((a.charm.title || t.charmTitle) + ' \u00b7 ' + s.finish, '', cv.price); }
    s.spares.forEach(function (name) { var bv = self.addonVariant(a.band, name); if (bv) row((a.band.title || t.spareTitle) + ' \u00b7 ' + name, '', bv.price); });
    if (s.packaging && a.packaging) row(a.packaging.title, '', a.packaging.price);
    if (s.card && a.card) row(a.card.title, '', a.card.price);
  };

  Builder.prototype.renderShipping = function (total) {
    var fs = this.cfg.freeShipping;
    if (!fs || !fs.enabled || !fs.threshold) { this.shipEl.hidden = true; return; }
    var have = total + (this.cartSubtotal || 0);
    this.shipEl.hidden = false;
    if (have >= fs.threshold) {
      this.shipEl.className = 'fgb-ship is-done';
      this.shipEl.textContent = this.t.freeShipYes;
    } else {
      this.shipEl.className = 'fgb-ship';
      this.shipEl.textContent = fmt(this.t.freeShipNo, { amount: formatMoney(fs.threshold - have, this.money) });
    }
  };

  Builder.prototype.syncAfterpay = function (total) {
    var amount = (total / 100).toFixed(2);
    this.$all('afterpay-placement, square-placement, [data-afterpay-amount]').forEach(function (n) {
      n.setAttribute('data-amount', amount);
    });
  };

  Builder.prototype.stepState = function () {
    var s = this.state, t = this.t, self = this;
    var fit = this.lastFit;
    this.steps.forEach(function (st) {
      var done = false, value = '';
      switch (st.key) {
        case 'band': done = !!s.band; value = s.band || ''; break;
        case 'finish': done = !!s.finish; value = s.finish || ''; break;
        case 'charm': done = s.charm !== null; value = s.charm === true ? t.charmAdded : s.charm === false ? t.charmNone : ''; break;
        case 'engraving':
          var n = self.engravingLines().length;
          done = n > 0 && fit && fit.level !== 'tiny';
          value = n === 1 ? t.lineOne : n ? fmt(t.lineMany, { n: n }) : '';
          break;
        case 'font': done = true; value = self.fontObj().label; break;
        case 'extras':
          done = true;
          var c = s.spares.length + (s.packaging ? 1 : 0) + (s.card ? 1 : 0);
          value = c ? c + ' added' : t.optional;
          break;
      }
      st.el.classList.toggle('is-done', !!done);
      st.value.textContent = value;
      st.num.textContent = done ? '' : st.index;
      if (done && !st.num.firstChild) st.num.appendChild(self.icon('check'));
    });
  };

  Builder.prototype.clearErrors = function (keys) {
    var map = { band: 'band', finish: 'finish', charm: 'charm', text: 'engraving', confirmed: 'confirm' };
    var self = this;
    keys.forEach(function (k) {
      var key = map[k];
      if (!key) return;
      if (key === 'confirm') { if (self.confirmWrap) self.confirmWrap.classList.remove('is-error'); return; }
      self.steps.forEach(function (st) {
        if (st.key === key) { st.el.classList.remove('is-error'); st.msg.hidden = true; }
      });
    });
    this.atcMsg.hidden = true;
  };

  Builder.prototype.validate = function () {
    var s = this.state, t = this.t;
    var v = this.variant();
    var errs = [];
    if (!s.band) errs.push(['band', t.needBand]);
    if (!s.finish) errs.push(['finish', t.needFinish]);
    if (this.addons.charm && s.charm === null) errs.push(['charm', t.needCharm]);
    var n = this.engravingLines().length;
    if (this.eng.required && !n) errs.push(['engraving', t.needText]);
    if (n && this.lastFit && this.lastFit.level === 'tiny') errs.push(['engraving', t.sizeTooSmall + '. ' + t.tooSmallHelp]);
    if (!v || !v.available) errs.push(['finish', t.unavailable]);
    if (this.confirmInput && !s.confirmed) errs.push(['confirm', t.needConfirm]);
    return errs;
  };

  Builder.prototype.showErrors = function (errs) {
    var self = this, first = null;
    errs.forEach(function (e) {
      if (e[0] === 'confirm') {
        self.confirmWrap.classList.add('is-error');
        first = first || self.confirmWrap;
        return;
      }
      self.steps.forEach(function (st) {
        if (st.key === e[0]) {
          st.el.classList.add('is-error');
          st.msg.textContent = e[1];
          st.msg.hidden = false;
          first = first || st.el;
        }
      });
    });
    this.atcMsg.textContent = errs[0][1];
    this.atcMsg.hidden = false;
    if (first) {
      var y = first.getBoundingClientRect().top + window.pageYOffset - (this.cfg.scrollOffset || 110);
      window.scrollTo({ top: y, behavior: 'smooth' });
      if (first.getAttribute('data-step') === 'engraving') setTimeout(function () { try { self.textarea.focus({ preventScroll: true }); } catch (e) { /* ignore */ } }, 450);
    }
  };

  /* ------------------------------------------------------------------ *
   * Cart
   * ------------------------------------------------------------------ */
  Builder.prototype.buildItems = function () {
    var s = this.state, a = this.addons, e = this.eng, self = this;
    var v = this.variant();
    var link = 'FG-' + Date.now().toString(36).toUpperCase() + Math.random().toString(36).slice(2, 5).toUpperCase();
    var engr = this.engravingLines();
    var props = {};
    engr.forEach(function (l, i) { props[e.lineLabel + ' ' + (i + 1)] = l; });
    props[e.fontLabel] = this.fontObj().engrave;
    props._fg = link;
    props._engraving = engr.join('\n');
    var who = (engr[0] ? engr[0] + ' \u00b7 ' : '') + s.band + ' / ' + s.finish;
    var items = [{ id: v.id, quantity: 1, properties: props }];
    if (s.charm === true) {
      var cv = this.charmVariant();
      if (cv) {
        var cp = {};
        cp[this.t.forLabel] = who;
        cp._fg = link;
        items.push({ id: cv.id, quantity: 1, properties: cp });
      }
    }
    s.spares.forEach(function (name) {
      var bv = self.addonVariant(a.band, name);
      if (bv) items.push({ id: bv.id, quantity: 1, properties: { _fg: link } });
    });
    if (s.packaging && a.packaging) items.push({ id: a.packaging.id, quantity: 1, properties: { _fg: link } });
    if (s.card && a.card) items.push({ id: a.card.id, quantity: 1, properties: { _fg: link } });
    // Shopify puts the newest line at the top of the cart, so send the
    // bracelet last: it then sits above its own add-ons.
    return items.reverse();
  };

  Builder.prototype.addToCart = function () {
    var self = this, t = this.t;
    if (this.busy) return;
    var errs = this.validate();
    if (errs.length) { this.showErrors(errs); return; }
    var items = this.buildItems();
    var total = this.total();
    this.busy = true;
    this.atc.classList.add('is-busy');
    this.atc.querySelector('.fgb-atc__label').textContent = t.adding;

    var done = function () {
      self.busy = false;
      self.atc.classList.remove('is-busy');
      self.atc.querySelector('.fgb-atc__label').textContent = t.addToCart;
    };

    if (this.cfg.mock) {
      this.root.dispatchEvent(new CustomEvent('fgb:mock-add', { bubbles: true, detail: { items: items, total: total } }));
      setTimeout(done, 400);
      return;
    }

    fetch(this.cfg.routes.cartAdd, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: items })
    }).then(function (r) {
      return r.json().then(function (j) { return { ok: r.ok, json: j }; });
    }).then(function (res) {
      if (!res.ok) throw new Error((res.json && (res.json.description || res.json.message)) || t.error);
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'flexguard_add_to_cart', value: total / 100, currency: self.cfg.currency, band: self.state.band, finish: self.state.finish, charm: self.state.charm === true });
      } catch (e) { /* ignore */ }
      self.root.dispatchEvent(new CustomEvent('fgb:added', { bubbles: true, detail: { items: items, response: res.json } }));
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      if (self.cfg.afterAdd === 'cart' && !self.cfg.designMode) {
        window.location.href = self.cfg.routes.cart;
        return;
      }
      done();
      self.atcMsg.textContent = t.added;
      self.atcMsg.classList.add('is-ok');
      self.atcMsg.hidden = false;
      self.fetchCart();
    }).catch(function (err) {
      done();
      self.atcMsg.classList.remove('is-ok');
      self.atcMsg.textContent = err && err.message ? err.message : t.error;
      self.atcMsg.hidden = false;
    });
  };

  Builder.prototype.fetchCart = function () {
    var self = this;
    var fs = this.cfg.freeShipping;
    if (this.cfg.mock || !fs || !fs.enabled || !window.fetch) return;
    fetch(this.cfg.routes.cartJs, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (c) { self.cartSubtotal = c.items_subtotal_price || c.total_price || 0; self.renderShipping(self.total()); })
      .catch(function () { /* ignore */ });
  };

  /* ------------------------------------------------------------------ *
   * Media: combination photo or gallery photo on top, zoom, URL
   * ------------------------------------------------------------------ */
  // Photo for the current band + finish, with or without the charm
  Builder.prototype.comboUrl = function () {
    var cp = this.cfg.comboPhotos;
    if (!this.combosOn || !cp) return null;
    var pair = cp[this.state.band + '|' + this.state.finish];
    if (!pair) return null;
    var url = this.state.charm === false ? (pair[1] || pair[0]) : pair[0];
    return url && !this.comboFailed[url] ? url : null;
  };

  Builder.prototype.applyCombo = function () {
    var url = this.comboUrl();
    if (!url || !this.shotCombo) return false;
    if (this.shotCombo.getAttribute('src') !== url) this.shotCombo.setAttribute('src', url);
    var s = this.state;
    this.shotCombo.alt = fmt(s.charm === false ? this.t.comboAlt : this.t.comboAltCharm, { band: s.band, finish: s.finish });
    return true;
  };

  Builder.prototype.updateComboThumb = function () {
    if (!this.comboThumbImg) return;
    var url = this.comboUrl();
    if (!url) { this.comboThumb.hidden = true; return; }
    if (this.comboThumbImg.getAttribute('src') !== url) this.comboThumbImg.setAttribute('src', url);
  };

  // Top panel shows the photo of their exact combination
  Builder.prototype.showCombo = function () {
    if (!this.shot) return;
    if (!this.applyCombo()) { this.showPhoto(null); return; }
    this.state.view = 'combo';
    this.syncView();
  };

  // Top panel shows a gallery photo; with no button, the product's main photo
  Builder.prototype.showPhoto = function (btn) {
    if (!this.shot) return;
    var img = this.shotPhoto;
    var src = btn ? btn.getAttribute('data-src') : img && (img.getAttribute('data-src') || img.getAttribute('src'));
    if (!img || !src) { this.state.view = 'none'; this.syncView(); return; }
    if (img.getAttribute('src') !== src) img.setAttribute('src', src);
    img.alt = (btn ? btn.getAttribute('data-alt') : img.getAttribute('data-alt')) || '';
    this.state.view = 'photo';
    this.syncView();
  };

  Builder.prototype.syncView = function () {
    var view = this.state.view;
    if (!this.shot) return;
    this.shot.hidden = view === 'none';
    this.shot.classList.toggle('is-combo', view === 'combo');
    this.shot.classList.toggle('is-photo', view === 'photo');
    if (this.backBtn) this.backBtn.hidden = !(view === 'photo' && this.comboUrl());
    var photoSrc = this.shotPhoto ? this.shotPhoto.getAttribute('src') : '';
    this.$all('[data-fgb-thumb]').forEach(function (b) {
      var on = b.getAttribute('data-fgb-thumb') === 'combo' ? view === 'combo' : view === 'photo' && b.getAttribute('data-src') === photoSrc;
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  };

  Builder.prototype.toggleZoom = function () {
    this.state.zoom = !this.state.zoom;
    if (this.stage) this.stage.classList.toggle('is-zoomed', this.state.zoom);
    this.zoomBtn.setAttribute('aria-pressed', this.state.zoom ? 'true' : 'false');
    this.zoomBtn.setAttribute('aria-label', this.state.zoom ? this.t.zoomOut : this.t.zoomIn);
    this.zoomBtn.title = this.state.zoom ? this.t.zoomOut : this.t.zoomIn;
    this.zoomBtn.innerHTML = '';
    this.zoomBtn.appendChild(this.icon(this.state.zoom ? 'unzoom' : 'zoom'));
    this.previews.forEach(function (p) { p.update(); });
  };

  Builder.prototype.syncUrl = function () {
    if (this.cfg.mock || this.cfg.updateUrl === false || !window.history || !history.replaceState) return;
    var v = this.variant();
    if (!v) return;
    try {
      var url = new URL(window.location.href);
      url.searchParams.set('variant', v.id);
      history.replaceState(history.state, '', url.toString());
    } catch (e) { /* ignore */ }
  };

  /* ------------------------------------------------------------------ *
   * Fonts
   * ------------------------------------------------------------------ */
  Builder.prototype.loadFont = function (font) {
    var self = this;
    if (!document.fonts || !document.fonts.load || !font) return;
    document.fonts.load('100px ' + font.css).then(function () {
      Measure.reset();
      self.update();
    }).catch(function () { /* ignore */ });
  };

  Builder.prototype.loadFonts = function () {
    var self = this;
    this.loadFont(this.fontObj());
    // Probe whether the real font is installed for faces with a web twin.
    Object.keys(FACES).forEach(function (k) {
      var f = FACES[k];
      if (!f.local || f._probed || !window.FontFace) return;
      f._probed = true;
      var src = f.local.map(function (n) { return 'local("' + n + '")'; }).join(', ');
      try {
        new FontFace('fgb-probe-' + k, src).load().then(function () { f._localOk = true; self.update(); }, function () { f._localOk = false; self.update(); });
      } catch (e) { f._localOk = false; }
    });
    // Warm the rest so font cards and switching are instant.
    var warm = function () { self.fonts.forEach(function (f) { if (document.fonts && document.fonts.load) document.fonts.load('16px ' + f.css).catch(function () {}); }); };
    if ('requestIdleCallback' in window) requestIdleCallback(warm, { timeout: 2500 }); else setTimeout(warm, 1200);
    if (document.fonts && document.fonts.addEventListener) {
      document.fonts.addEventListener('loadingdone', function () { Measure.reset(); self.update(); });
    }
  };

  /* ------------------------------------------------------------------ *
   * Preview (SVG, units of 0.1 mm)
   * ------------------------------------------------------------------ */
  function Preview(b, mount, opts) {
    this.b = b;
    this.mount = mount;
    this.fixedZoom = !!opts.fixedZoom;
    var id = b.uid + '-' + (opts.suffix || 'p');
    this.id = id;
    var W = STAGE.w * U, H = STAGE.h * U;
    this.vb = this.fixedZoom ? this.zoomBox(0, false) : [0, 0, W, H];
    var PW = b.eng.plateW * U, PH = b.eng.plateH * U;
    this.PW = PW; this.PH = PH;

    var svg = sv('svg', { class: 'fgb-svg', viewBox: this.vb.join(' '), preserveAspectRatio: 'xMidYMid meet', role: 'img', 'aria-label': b.t.livePreview, focusable: 'false' });
    var defs = sv('defs');

    // band gradient (across the band width)
    this.bandStops = [];
    var bg = sv('linearGradient', { id: id + '-bg', x1: 0, y1: 0, x2: 0, y2: 1 });
    [0, 0.07, 0.22, 0.55, 0.88, 1].forEach(function (o) { var st = sv('stop', { offset: o }); bg.appendChild(st); this.bandStops.push(st); }, this);
    // fallback plate gradient + sheen
    this.plateStops = [];
    var pg = sv('linearGradient', { id: id + '-pg', x1: 0, y1: 0, x2: 0.3, y2: 1 });
    [0, 0.45, 1].forEach(function (o) { var st = sv('stop', { offset: o }); pg.appendChild(st); this.plateStops.push(st); }, this);
    var sheen = sv('linearGradient', { id: id + '-sh', x1: 0, y1: 0, x2: 1, y2: 1 }, [
      sv('stop', { offset: 0.25, 'stop-color': '#fff', 'stop-opacity': 0 }),
      sv('stop', { offset: 0.48, 'stop-color': '#fff', 'stop-opacity': 0.35 }),
      sv('stop', { offset: 0.7, 'stop-color': '#fff', 'stop-opacity': 0 })
    ]);
    var drop = sv('filter', { id: id + '-ds', x: '-15%', y: '-15%', width: '130%', height: '150%' }, [
      sv('feDropShadow', { dx: 0, dy: 3.5, stdDeviation: 4.5, 'flood-color': '#000', 'flood-opacity': 0.34 })
    ]);
    var bandDrop = sv('filter', { id: id + '-bd', x: '-5%', y: '-40%', width: '110%', height: '200%' }, [
      sv('feDropShadow', { dx: 0, dy: 7, stdDeviation: 9, 'flood-color': '#2b2620', 'flood-opacity': 0.16 })
    ]);
    var grain = sv('filter', { id: id + '-gr', x: 0, y: 0, width: '100%', height: '100%' }, [
      sv('feTurbulence', { type: 'fractalNoise', baseFrequency: 0.85, numOctaves: 2, seed: 4, result: 'n' }),
      sv('feColorMatrix', { type: 'matrix', values: '0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0.9 -0.42' })
    ]);
    var plateClip = sv('clipPath', { id: id + '-pc' }, [sv('rect', { x: 0, y: 0, width: PW, height: PH, rx: PLATE_R * U, ry: PLATE_R * U })]);
    var charmClip = sv('clipPath', { id: id + '-cc' }, [sv('rect', { x: 0, y: 0, width: CHARM.w * U, height: CHARM.h * U, rx: CHARM.r * U, ry: CHARM.r * U })]);
    append(defs, [bg, pg, sheen, drop, bandDrop, grain, plateClip, charmClip]);
    svg.appendChild(defs);

    // band
    var by = (H - BAND_W * U) / 2;
    this.gBand = sv('g', { filter: 'url(#' + id + '-bd)' }, [
      sv('rect', { x: -400, y: by, width: W + 800, height: BAND_W * U, fill: 'url(#' + id + '-bg)' }),
      sv('rect', { x: -400, y: by, width: W + 800, height: BAND_W * U, fill: '#000', filter: 'url(#' + id + '-gr)', opacity: 0.18 }),
      sv('rect', { x: -400, y: by + 3, width: W + 800, height: 1.6, fill: '#fff', opacity: 0.22 })
    ]);
    svg.appendChild(this.gBand);

    // plate
    this.gPlate = sv('g', { class: 'fgb-svg__plate' });
    this.plateArt = sv('g', { filter: 'url(#' + id + '-ds)' });
    this.plateImg = sv('image', { x: 0, y: 0, width: PW, height: PH, preserveAspectRatio: 'none', 'clip-path': 'url(#' + id + '-pc)' });
    this.plateFallback = sv('g', {}, [
      sv('rect', { x: 0, y: 0, width: PW, height: PH, rx: PLATE_R * U, fill: 'url(#' + id + '-pg)' }),
      sv('rect', { x: 0, y: 0, width: PW, height: PH, rx: PLATE_R * U, fill: 'url(#' + id + '-sh)' })
    ]);
    append(this.plateArt, [this.plateFallback, this.plateImg]);
    this.gEngr = sv('g', { 'clip-path': 'url(#' + id + '-pc)' });
    this.gHl = sv('g', {});
    this.gInk = sv('g', {});
    append(this.gEngr, [this.gHl, this.gInk]);
    append(this.gPlate, [this.plateArt, this.gEngr]);
    svg.appendChild(this.gPlate);

    // charm
    this.gCharm = sv('g', { class: 'fgb-svg__charm' });
    this.charmArt = sv('g', { filter: 'url(#' + id + '-ds)' });
    this.charmImg = sv('image', { x: 0, y: 0, width: CHARM.w * U, height: CHARM.h * U, preserveAspectRatio: 'none', 'clip-path': 'url(#' + id + '-cc)' });
    this.charmFallback = this.drawCharm(id);
    append(this.charmArt, [this.charmFallback, this.charmImg]);
    this.gCharm.appendChild(this.charmArt);
    svg.appendChild(this.gCharm);

    this.svg = svg;
    mount.appendChild(svg);
    this.imgState = {};
  }

  Preview.prototype.drawCharm = function (id) {
    var w = CHARM.w * U, hh = CHARM.h * U, cx = w / 2, cy = hh / 2;
    var g = sv('g', {});
    g.appendChild(sv('rect', { x: 0, y: 0, width: w, height: hh, rx: CHARM.r * U, fill: 'url(#' + id + '-pg)' }));
    var star = sv('g', { transform: 'translate(' + cx + ' ' + cy + ')' });
    // Same look as the real charm: flat red star, rod and snake cut out to show the metal
    for (var i = 0; i < 3; i++) {
      star.appendChild(sv('rect', { x: -9, y: -27, width: 18, height: 54, rx: 1.6, fill: '#e31b2d', transform: 'rotate(' + (i * 60) + ')' }));
    }
    this.cadPaths = [
      sv('path', { d: 'M0 -20 L0 20', stroke: '#c9ccd1', 'stroke-width': 2.4, 'stroke-linecap': 'round' }),
      sv('path', { d: 'M-4 -12 C 5 -9, 5 -4, 0 -2 C -5 0, -5 5, 0 7 C 5 9, 5 13, 1 15', stroke: '#c9ccd1', 'stroke-width': 2, fill: 'none', 'stroke-linecap': 'round' })
    ];
    append(star, this.cadPaths);
    g.appendChild(star);
    return g;
  };

  Preview.prototype.layout = function (withCharm) {
    var e = this.b.eng;
    var total = withCharm ? e.plateW + CHARM.gap + CHARM.w : e.plateW;
    var x0 = (STAGE.w - total) / 2;
    var py = (STAGE.h - e.plateH) / 2;
    return {
      plate: { x: x0 * U, y: py * U },
      charm: { x: (x0 + e.plateW + CHARM.gap) * U, y: ((STAGE.h - CHARM.h) / 2 + CHARM.dy) * U }
    };
  };

  // Zoomed view box. Inline preview: the whole plate. Main preview: the engraving area.
  Preview.prototype.zoomBox = function (plateX) {
    var e = this.b.eng;
    var cx = plateX + (e.plateW * U) / 2;
    var cy = STAGE.h * U / 2;
    var w, hgt;
    if (this.fixedZoom) {
      w = (e.plateW + 5) * U;
      hgt = w * 0.62;
    } else {
      var aspect = STAGE.w / STAGE.h;
      w = Math.max(e.areaW + 8, (e.areaH + 2) * aspect) * U;
      hgt = w / aspect;
    }
    return [cx - w / 2, cy - hgt / 2, w, hgt];
  };

  Preview.prototype.setImage = function (img, url, fallback, okKey) {
    var self = this;
    if (!url) { img.style.display = 'none'; fallback.style.display = ''; return; }
    if (this.imgState[url] === 'error') { img.style.display = 'none'; fallback.style.display = ''; return; }
    if (img.getAttribute('href') !== url) {
      img.setAttribute('href', url);
      if (!this.imgState[url]) {
        var probe = new Image();
        probe.onload = function () { self.imgState[url] = 'ok'; };
        probe.onerror = function () {
          self.imgState[url] = 'error';
          img.style.display = 'none';
          fallback.style.display = '';
          clearTimeout(self.b._imgT);
          self.b._imgT = setTimeout(function () { self.b.update(); }, 30);
        };
        probe.src = url;
        this.imgState[url] = 'loading';
      }
    }
    img.style.display = '';
    fallback.style.display = 'none';
  };

  Preview.prototype.update = function () {
    var b = this.b, s = b.state, e = b.eng;
    var band = b.bandObj(), fin = b.finishObj();
    if (!band || !fin) return;
    // Until they choose, show the charm, same as the combination photo above
    var withCharm = s.charm === true || (s.charm === null && !!b.addons.charm);
    var lay = this.layout(withCharm);

    // band colours
    var c = band.hex;
    var lum = luminance(c);
    var edge = lum > 0.6 ? -0.2 : -0.32;
    var stops = [shade(c, edge), shade(c, -0.05), shade(c, lum > 0.6 ? 0.04 : 0.08), c, shade(c, -0.06), shade(c, edge - 0.02)];
    this.bandStops.forEach(function (st, i) { st.setAttribute('stop-color', stops[i]); });

    // plate + charm art
    this.plateStops[0].setAttribute('stop-color', fin.light);
    this.plateStops[1].setAttribute('stop-color', mix(fin.light, fin.dark, 0.3));
    this.plateStops[2].setAttribute('stop-color', fin.dark);
    this.setImage(this.plateImg, fin.plate, this.plateFallback);
    this.setImage(this.charmImg, fin.charm, this.charmFallback);
    var cutColour = mix(fin.light, fin.dark, 0.35);
    (this.cadPaths || []).forEach(function (p) { p.setAttribute('stroke', cutColour); });
    this.gPlate.style.transform = 'translate(' + lay.plate.x + 'px,' + lay.plate.y + 'px)';
    this.gCharm.style.transform = 'translate(' + lay.charm.x + 'px,' + lay.charm.y + 'px)' + (withCharm ? '' : ' translateY(40px)');
    this.gCharm.style.opacity = withCharm ? 1 : 0;

    // engraving
    this.drawText(fin);

    // view box
    var target = (this.fixedZoom || s.zoom) ? this.zoomBox(lay.plate.x, withCharm) : [0, 0, STAGE.w * U, STAGE.h * U];
    this.animateTo(target);
  };

  Preview.prototype.drawText = function (fin) {
    var b = this.b, e = b.eng;
    var real = b.engravingLines();
    var isExample = !real.length;
    var ls = isExample ? lines(e.placeholder) : real;
    this.gHl.innerHTML = '';
    this.gInk.innerHTML = '';
    if (!ls.length) return;
    var font = b.fontObj();
    var fit = isExample ? b.fit(ls, font) : b.lastFit;
    if (!fit) return;
    var light = luminance(fin.text) > 0.4;
    var ink = fin.text;
    var fsU = fit.fs * U;
    var lhU = fit.lh * fsU;
    var cx = this.PW / 2;
    var face = this.b.fontObj().face || {};
    var faceOffset = fin.plateOk && fin.plate && this.imgState[fin.plate] !== 'error' ? e.faceOffset : 0;
    var cy = this.PH / 2 + faceOffset * U;
    var n = ls.length;
    var stroke = face.twinStroke && !face._localOk ? face.twinStroke * fsU : 0;
    var self = this;
    this.gInk.setAttribute('fill', ink);
    this.gInk.style.mixBlendMode = light ? 'screen' : 'multiply';
    this.gInk.setAttribute('opacity', isExample ? 0.32 : 0.9);
    this.gHl.setAttribute('fill', light ? '#000' : '#fff');
    this.gHl.setAttribute('opacity', isExample ? 0 : light ? 0.45 : 0.4);
    this.gHl.setAttribute('transform', 'translate(0 ' + (light ? -0.5 : 0.55) + ')');
    ls.forEach(function (l, i) {
      var y = cy + (i - (n - 1) / 2) * lhU + (fit.capRatio * fsU) / 2;
      [self.gHl, self.gInk].forEach(function (g) {
        var tx = sv('text', { x: cx, y: y, 'text-anchor': 'middle', 'font-size': fsU.toFixed(2), style: 'font-family:' + font.stack + ';white-space:pre' });
        tx.setAttributeNS(XMLNS, 'xml:space', 'preserve');
        if (stroke) { tx.setAttribute('stroke', g === self.gInk ? ink : (light ? '#000' : '#fff')); tx.setAttribute('stroke-width', stroke.toFixed(2)); tx.setAttribute('stroke-linejoin', 'round'); }
        tx.textContent = l;
        g.appendChild(tx);
      });
    });
  };

  Preview.prototype.animateTo = function (target) {
    var self = this;
    var from = this.vb.slice();
    var same = from.every(function (v, i) { return Math.abs(v - target[i]) < 0.01; });
    if (same) { this._painted = true; return; }
    cancelAnimationFrame(this._raf);
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || !this._painted) {
      this.vb = target.slice();
      this.svg.setAttribute('viewBox', this.vb.join(' '));
      this._painted = true;
      return;
    }
    var t0 = performance.now(), dur = 420;
    var step = function (now) {
      var k = clamp((now - t0) / dur, 0, 1);
      var ease = k < 0.5 ? 2 * k * k : 1 - Math.pow(-2 * k + 2, 2) / 2;
      self.vb = from.map(function (v, i) { return v + (target[i] - v) * ease; });
      self.svg.setAttribute('viewBox', self.vb.join(' '));
      if (k < 1) self._raf = requestAnimationFrame(step);
    };
    this._raf = requestAnimationFrame(step);
  };

  /* ------------------------------------------------------------------ *
   * Boot
   * ------------------------------------------------------------------ */
  function boot(scope) {
    (scope || document).querySelectorAll('[data-fgb]').forEach(function (root) {
      if (root.__fgb) return;
      try { root.__fgb = new Builder(root); }
      catch (err) { if (window.console) console.error('[FlexGuard] builder failed to start', err); }
    });
  }
  window.FlexGuardBuilder = { boot: boot, Builder: Builder, formatMoney: formatMoney };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { boot(); });
  else boot();
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
})();
